import {
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ReactiveIconTextButton } from '../../components/reactive-icon-text-button/reactive-icon-text-button';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InternalToolbar } from '../../components/internal-toolbar/internal-toolbar';
import { ApiService } from '../../api.service';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { TAGTodo, TodoPriority, TodoStatus } from '../../../models/tagtodo';
import { MessageService } from 'primeng/api';
import { TAGUser } from '../../../models/taguser';
import { CdkDrag, DragDropModule } from '@angular/cdk/drag-drop';
import { MatIcon } from '../../components/mat-icon/mat-icon';
import { DatePipe } from '@angular/common';
import { SkeletonModule } from 'primeng/skeleton';
import { InputMaskModule } from 'primeng/inputmask';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { TodoEditDialog, TodoEditDialogData } from './todoEditDialog';
import { dialogConfig } from '../../dialogs';

interface SelectedTodoData {
  element: HTMLElement;
  todo: TAGTodo;
  x: number;
  y: number;
}

@Component({
  selector: 'app-internal-todos',
  imports: [
    ReactiveIconTextButton,
    ToggleSwitchModule,
    InternalToolbar,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TranslatePipe,
    FormsModule,
    FloatLabelModule,
    CdkDrag,
    DragDropModule,
    MatIcon,
    DatePipe,
    SkeletonModule,
    InputMaskModule,
    ButtonModule,
  ],
  providers: [DialogService],
  templateUrl: './internal-todos.html',
  styleUrl: './internal-todos.scss',
})
export class InternalTodos {
  private messageService = inject(MessageService);
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  public api = inject(ApiService);

  @ViewChild('todoMain') todoMain: ElementRef<HTMLElement>;
  @ViewChildren('swimlane') swimlane: QueryList<ElementRef>;
  swimlanes: ElementRef[] = [];

  users = signal<TAGUser[]>([]);
  loadingUsers = signal(true);
  todos = signal<TAGTodo[]>([]);
  loadingTodos = signal(true);
  reloadTimer: any = undefined;

  // Drag'n'Drop Stuff
  isDragging = signal(false);

  selectedTodo = signal<SelectedTodoData>(undefined);

  searchFilter = signal('');
  highlightedTodos = computed(() => {
    if (this.searchFilter().trim() == '') {
      return undefined;
    }

    return this.todos()
      .filter((t) => {
        return (
          t.description.toLowerCase().includes(this.searchFilter().toLowerCase()) ||
          this.userToName(t.assignee).toLowerCase().includes(this.searchFilter().toLowerCase()) ||
          t.status.name.toLowerCase().includes(this.searchFilter().toLowerCase()) ||
          t.priority.name.toLowerCase().includes(this.searchFilter().toLowerCase())
        );
      })
      .map((t) => t._id);
  });

  showTodoMenu = signal(false);
  isMoving = signal(false);
  selectedMenuItem = signal('priority');

  todoCopy: TAGTodo;
  moveStartPosition: { x: number; y: number } = { x: 0, y: 0 };
  menuItems: string[] = ['priority', 'move'];

  get allStatus() {
    return TodoStatus.ALL;
  }

  get allPriorities() {
    return TodoPriority.ALL;
  }

  get dragHandleDisabled() {
    return screen.width >= 1000;
  }

  constructor() {}

  @HostListener('window:resize')
  updateOverlayRect() {
    if (!this.todoMain) return;
    const rect = this.todoMain.nativeElement.getBoundingClientRect();
    const el = this.todoMain.nativeElement;
    el.style.setProperty('--overlay-top', `${rect.top}px`);
    el.style.setProperty('--overlay-left', `${rect.left}px`);
    el.style.setProperty('--overlay-width', `${rect.width}px`);
    el.style.setProperty('--overlay-height', `${rect.height}px`);

    console.log(el.style.getPropertyValue('--overlay-top'));
  }

  todosByState = computed(() => {
    let sorted: { [state: string]: TAGTodo[] } = {};
    for (let todo of this.todos()) {
      if (!Object.keys(sorted).includes(todo.status.name)) {
        sorted[todo.status.name] = [];
      }

      sorted[todo.status.name].push(todo);
    }
    return sorted;
  });

  tooltipPos = computed(() => {
    if (!this.selectedTodo()) return false;
    return (
      this.todosByState()[this.selectedTodo().todo.status.name].findIndex(
        (t) => t._id == this.selectedTodo().todo._id,
      ) == 0
    );
  });

  userToName(id: string) {
    return this.users().find((u) => u._id == id)?.fullname ?? id;
  }

  duedateWarning(date: Date) {
    let diff = date.getTime() - new Date().getTime();
    if (diff < 0) {
      return '#f44336';
    } else if (diff < 1000 * 60 * 60 * 48) {
      // 2days
      return '#ff9800'; //#ffc107
    }

    return undefined;
  }

  ngOnInit() {
    this.api.waitForAPI().then(() => {
      this.api
        .getUsers()
        .then((users: TAGUser[]) => {
          this.users.set(users);
          this.loadingUsers.set(false);
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.translate('errors.couldNotLoadUsers')(),
            detail: reason.message,
          });
        });

      this.loadTodos();
      this.toggleAutoReload(true);
    });
  }

  ngAfterViewInit(): void {
    this.swimlanes = this.swimlane.toArray();
    console.log(this.swimlanes);
    this.updateOverlayRect();
  }

  ngOnDestroy() {
    if (this.reloadTimer) clearInterval(this.reloadTimer);
    this.reloadTimer = undefined;
  }

  loadTodos() {
    if (this.isDragging() || this.showTodoMenu() || this.isMoving()) return;

    this.api
      .getTodos()
      .then((todos: TAGTodo[]) => {
        this.todos.set(
          todos.sort((a, b) => TodoPriority.toValue(b.priority) - TodoPriority.toValue(a.priority)),
        );

        if (this.selectedTodo()) {
          let newTodo = this.todos().find((t) => t._id == this.selectedTodo().todo._id);
          let newX = 0;
          let status = this.selectedTodo().todo.status;
          switch (status) {
            case TodoStatus.OPEN:
              newX = 0;
              break;
            case TodoStatus.INPROGRESS:
              newX = 1;
              break;
            case TodoStatus.DONE:
              newX = 2;
              break;
            case TodoStatus.CANCELED:
              newX = 3;
              break;
          }

          this.selectedTodo.update((t) => {
            let tmp = Object.assign({}, t);
            tmp.todo = newTodo;
            tmp.x = newX;
            tmp.y = this.todosByState()[status.name].findIndex(
              (t) => t._id == this.selectedTodo().todo._id,
            );
            return tmp;
          });
        }

        console.log(todos);
        this.loadingTodos.set(false);
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('errors.couldNotLoadTodos')(),
          detail: reason.message,
        });
      });
  }

  onKey(event: any) {
    console.log(event);
    if (!event.getModifierState('NumLock')) return;

    if (this.selectedTodo() == undefined) {
      this.setSelected(0, 0);
      return;
    }

    this.setSelected(this.selectedTodo().x, this.selectedTodo().y);

    if (!this.showTodoMenu() && !this.isMoving()) {
      if (event.code === 'Numpad2') {
        this.setSelected(this.selectedTodo().x, this.selectedTodo().y + 1);
        event.preventDefault();
      } else if (event.code === 'Numpad8') {
        this.setSelected(this.selectedTodo().x, this.selectedTodo().y - 1);
        event.preventDefault();
      } else if (event.code === 'Numpad4') {
        this.setSelected(this.selectedTodo().x - 1, this.selectedTodo().y);
        event.preventDefault();
      } else if (event.code === 'Numpad6') {
        this.setSelected(this.selectedTodo().x + 1, this.selectedTodo().y);
        event.preventDefault();
      } else if (event.code === 'Numpad5' || event.code === 'NumpadEnter') {
        this.showTodoMenu.set(true);
        this.selectedMenuItem.set(this.menuItems[0]);
        this.todoCopy = new TAGTodo(Object.assign({}, this.selectedTodo().todo.serialize()));
        event.preventDefault();
      }
    } else if (this.isMoving()) {
      if (event.code === 'Numpad4') {
        this.setTodoStatus(
          this.todos().find((t) => t._id == this.selectedTodo().todo._id),
          false,
        );
        event.preventDefault();
      } else if (event.code === 'Numpad6') {
        this.setTodoStatus(
          this.todos().find((t) => t._id == this.selectedTodo().todo._id),
          true,
        );
        event.preventDefault();
      } else if (event.code === 'Numpad5' || event.code === 'NumpadEnter') {
        this.isMoving.set(false);
        this.saveTodo(this.selectedTodo().todo);
        event.preventDefault();
      } else if (event.code === 'Numpad0') {
        this.isMoving.set(false);
        let index = this.todos().findIndex((t) => t._id == this.selectedTodo().todo._id);
        this.todos.update((t) => {
          t.splice(index, 1, this.todoCopy);
          return [...t];
        });
        this.selectedTodo.update((t) => {
          let tmp = Object.assign({}, t);
          tmp.todo = new TAGTodo(Object.assign({}, t.todo.serialize()));
          tmp.x = this.moveStartPosition.x;
          tmp.y = this.moveStartPosition.y;
          return tmp;
        });
        event.preventDefault();
      }
    } else {
      if (event.code === 'Numpad0') {
        this.showTodoMenu.set(false);
        let index = this.todos().findIndex((t) => t._id == this.selectedTodo().todo._id);
        this.todos.update((t) => {
          t.splice(index, 1, this.todoCopy);
          return [...t];
        });
        event.preventDefault();
      } else if (event.code === 'Numpad2') {
        this.selectMenuItem(true);
        event.preventDefault();
      } else if (event.code === 'Numpad8') {
        this.selectMenuItem(false);
        event.preventDefault();
      } else if (event.code === 'Numpad4') {
        if (this.selectedMenuItem() == 'priority') {
          this.setTodoPriority(
            this.todos().find((t) => t._id == this.selectedTodo().todo._id),
            false,
          );
        }
        event.preventDefault();
      } else if (event.code === 'Numpad6') {
        if (this.selectedMenuItem() == 'priority') {
          this.setTodoPriority(
            this.todos().find((t) => t._id == this.selectedTodo().todo._id),
            true,
          );
        }
        event.preventDefault();
      } else if (event.code === 'Numpad5' || event.code === 'NumpadEnter') {
        if (this.selectedMenuItem() == 'move') {
          this.isMoving.set(true);
          this.showTodoMenu.set(false);

          this.moveStartPosition.x = this.selectedTodo().x;
          this.moveStartPosition.y = this.selectedTodo().y;
        } else {
          this.saveTodo(this.selectedTodo().todo);
          this.showTodoMenu.set(false);
        }
        event.preventDefault();
      }

      /* this.selectedTodo.element.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });

      this.setMenuPosition(this.selectedTodo.element); */
    }
  }

  setSelected(x: number, y: number) {
    console.log(x, y);
    if (x < 0) x = 0;
    if (y <= 0) {
      y = 0;
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
    if (x > this.swimlanes.length - 1) x = this.swimlanes.length - 1;
    if (y > this.swimlanes[x].nativeElement.children.length - 2)
      y = this.swimlanes[x].nativeElement.children.length - 2;

    this.selectedTodo.set({
      element: this.swimlanes[x].nativeElement.children[y + 1],
      todo: this.todos().find(
        (t) => t._id == this.swimlanes[x].nativeElement.children[y + 1].getAttribute('data-todo'),
      ),
      x: x,
      y: y,
    });

    if (y > 0) {
      /* let windowTop = window.scrollY;
      let windowBottom = windowTop + window.innerHeight - 64;
      let todoTop = this.selectedTodo.element.getBoundingClientRect().top - 32;
      let todoBottom = this.selectedTodo.element.getBoundingClientRect().top + this.selectedTodo.element.getBoundingClientRect().height + 64;

      let scrollPos = 0;
      if (todoTop < windowTop) {
        scrollPos = todoTop - windowTop;
      } else if (todoBottom > windowBottom) {
        scrollPos = todoBottom - windowBottom;
      }

      if (scrollPos != 0) {
        window.scrollBy({
          top: scrollPos,
          behavior: "smooth",
        });
      } */

      this.selectedTodo().element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }

  selectMenuItem(next: boolean) {
    let currentIndex = this.menuItems.indexOf(this.selectedMenuItem());
    let newIndex = next ? currentIndex + 1 : currentIndex - 1;
    if (newIndex < 0) newIndex = 0;
    if (newIndex >= this.menuItems.length) newIndex = this.menuItems.length - 1;
    this.selectedMenuItem.set(this.menuItems[newIndex]);
  }

  setTodoPriority(todo: TAGTodo, higher: boolean) {
    let newPrio = todo.priority;
    switch (newPrio) {
      case TodoPriority.LOW:
        newPrio = higher ? TodoPriority.NORMAL : TodoPriority.LOW;
        break;
      case TodoPriority.NORMAL:
        newPrio = higher ? TodoPriority.HIGH : TodoPriority.LOW;
        break;
      case TodoPriority.HIGH:
        newPrio = higher ? TodoPriority.HIGH : TodoPriority.NORMAL;
        break;
    }

    this.todos.update((t) => {
      let newTodo = new TAGTodo(Object.assign({}, t.find((t2) => t2._id === todo._id).serialize()));
      newTodo.priority = newPrio;
      t.splice(
        t.findIndex((t2) => t2._id === todo._id),
        1,
        newTodo,
      );
      return [...t];
    });
  }

  setTodoStatus(todo: TAGTodo, higher: boolean) {
    let newStatus = todo.status;
    switch (newStatus) {
      case TodoStatus.OPEN:
        newStatus = higher ? TodoStatus.INPROGRESS : TodoStatus.OPEN;
        break;
      case TodoStatus.INPROGRESS:
        newStatus = higher ? TodoStatus.DONE : TodoStatus.OPEN;
        break;
      case TodoStatus.DONE:
        newStatus = higher ? TodoStatus.CANCELED : TodoStatus.INPROGRESS;
        break;
      case TodoStatus.CANCELED:
        newStatus = higher ? TodoStatus.CANCELED : TodoStatus.DONE;
        break;
    }

    this.todos.update((t) => {
      let newTodo = new TAGTodo(Object.assign({}, t.find((t2) => t2._id === todo._id).serialize()));
      newTodo.status = newStatus;
      t.splice(
        t.findIndex((t2) => t2._id === todo._id),
        1,
        newTodo,
      );
      return [...t];
    });

    if (higher && this.selectedTodo().x <= this.swimlanes.length - 1) {
      this.selectedTodo.update((t) => {
        let tmp = Object.assign({}, t);
        tmp.todo = new TAGTodo(Object.assign({}, t.todo.serialize()));
        tmp.x = tmp.x + 1;
        tmp.y = this.todosByState()[newStatus.name].findIndex((t) => t._id == todo._id);
        return tmp;
      });
    } else if (!higher && this.selectedTodo().x > 0) {
      this.selectedTodo.update((t) => {
        let tmp = Object.assign({}, t);
        tmp.todo = new TAGTodo(Object.assign({}, t.todo.serialize()));
        tmp.x = tmp.x - 1;
        tmp.y = this.todosByState()[newStatus.name].findIndex((t) => t._id == todo._id);
        return tmp;
      });
    }
  }

  toggleAutoReload(state: boolean) {
    if (state) {
      if (this.reloadTimer) clearInterval(this.reloadTimer);
      this.reloadTimer = setInterval(() => {
        this.loadTodos();
      }, 5000);
    } else {
      if (this.reloadTimer) clearInterval(this.reloadTimer);
      this.reloadTimer = undefined;
    }
  }

  openTodo(todo?: TAGTodo) {
    const dialogRef = this.dialogService.open(
      TodoEditDialog,
      dialogConfig<TodoEditDialogData>({
        data: {
          todo: todo,
        },
      }),
    );

    dialogRef.onClose.subscribe((result) => {
      if (result) this.loadTodos();
    });
  }

  saveTodo(todo: TAGTodo) {
    console.log(todo);
    this.api
      .updateTodo(todo)
      .then((result) => {
        this.loadTodos();
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('errors.couldNotUpdateTodo')(),
          detail: reason.message,
        });
      });
  }

  startDrag(event) {
    this.isDragging.set(true);
  }

  endDrag(event) {
    this.isDragging.set(false);
  }

  drop(event) {
    console.log(event);
    let todo: TAGTodo = event.item.data;
    let targetState: TodoStatus = TodoStatus.fromName(event.container.data.name);

    if (todo.status == targetState) {
      return;
    }

    todo.status = targetState;

    this.api
      .updateTodo(todo)
      .then((result) => {
        this.loadTodos();
      })
      .catch((reason) => {
        console.error(reason);
      });
  }
}
